<script lang="ts">
import { chunk, uniqueId } from 'lodash'
import { defineComponent, PropType } from 'vue'
import NrDatepickerDay from './nr-datepicker-day.vue'
import NrButton from '../NrButton/nr-button.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'

interface Data {
  focusDay: Date
}

const COMPONENT_NAME = 'NrDatepickerCalendar'
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

export default defineComponent({
  name: COMPONENT_NAME,
  components: { NrDatepickerDay, NrButton, NrIconButton },
  props: {
    date: { type: Date as PropType<Date>, required: true },
    minDate: { type: Date as PropType<Date>, default: undefined },
    maxDate: { type: Date as PropType<Date>, default: undefined },
    unselectables: {
      type: Array as PropType<number[]>,
      default() {
        return []
      }
    }
  },
  data(): Data {
    return {
      focusDay: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      )
    }
  },
  mounted() {
    if (this.date) {
      const date = new Date(
        this.date.getFullYear(),
        this.date.getMonth(),
        this.date.getDate()
      )
      this.setFocusDay(date)
    }
  },
  computed: {
    id(): string {
      return uniqueId(`${COMPONENT_NAME}-`)
    },
    dayOfWeek(): number {
      return this.firstDayOfMonth.getDay()
    },
    firstDayOfMonth(): Date {
      return new Date(this.focusDay.getFullYear(), this.focusDay.getMonth(), 1)
    },
    daysInMonth(): number {
      return new Date(
        this.focusDay.getFullYear(),
        this.focusDay.getMonth() + 1,
        0
      ).getDate()
    },
    firstsDateToRender(): Date {
      const date = new Date(this.firstDayOfMonth)
      date.setDate(this.firstDayOfMonth.getDate() - this.dayOfWeek)
      return date
    },
    getDates(): Date[][] {
      const DAYS_LENGTH = this.dayOfWeek + this.daysInMonth < 35 ? 35 : 42

      return chunk(
        Array.from(Array(DAYS_LENGTH)).map((_, index: number) => {
          const date = new Date(this.firstsDateToRender)
          date.setDate(this.firstsDateToRender.getDate() + index)
          return date
        }),
        7
      )
    },
    month(): string {
      return MONTH_LABELS[this.focusDay.getMonth()]
    },
    year(): number {
      return this.focusDay.getFullYear()
    }
  },
  methods: {
    isSelectedDate(date: Date): boolean {
      if (!this.date) {
        return false
      }

      const selectedDate = new Date(
        this.date.getFullYear(),
        this.date.getMonth(),
        this.date.getDate()
      )

      return date.toUTCString() === selectedDate.toUTCString()
    },
    isFocusDate(date: Date): boolean {
      return date.toUTCString() === this.focusDay.toUTCString()
    },
    isDisabled(date: Date): boolean {
      return !this.inFocusedMonth(date) || !this.inMinMaxDate(date)
    },
    inFocusedMonth(date: Date): boolean {
      return date.getMonth() === this.focusDay.getMonth()
    },
    inMinMaxDate(date: Date): boolean {
      if (this.minDate && this.maxDate) {
        return this.minDate <= date && this.maxDate >= date
      } else if (this.minDate) {
        return this.minDate <= date
      } else if (this.maxDate) {
        return this.maxDate >= date
      } else if (this.unselectables?.length) {
        return !this.unselectables.includes(Date.parse(date.toISOString()))
      }

      return true
    },
    selectDate(): void {
      this.$emit('selected', this.focusDay)
      this.$emit('hide')
    },
    moveFocusDay(direction = 1): void {
      const date = new Date(this.focusDay)
      date.setDate(date.getDate() + direction)
      this.setFocusDay(date)
    },
    moveFocusToFirstDayOfWeek(): void {
      this.moveFocusDay(-this.focusDay.getDay())
    },
    moveFocusToLastDayOfWeek(): void {
      this.moveFocusDay(6 - this.focusDay.getDay())
    },
    moveFocusToPreviousMonth(): void {
      const date = new Date(this.focusDay)
      date.setDate(1)
      date.setMonth(this.focusDay.getMonth() - 1)
      const difference =
        (this.focusDay.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      this.moveFocusDay(-difference)
    },
    moveFocusToNextMonth(): void {
      const date = new Date(this.focusDay)
      date.setDate(1)
      date.setMonth(this.focusDay.getMonth() + 1)
      const difference =
        (date.getTime() - this.focusDay.getTime()) / (1000 * 60 * 60 * 24)
      this.moveFocusDay(difference)
    },
    moveFocusToPreviousYear(): void {
      const date = new Date(this.focusDay)
      date.setDate(1)
      date.setFullYear(this.focusDay.getFullYear() - 1)
      const difference =
        (this.focusDay.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      this.moveFocusDay(-difference)
    },
    moveFocusToNextYear(): void {
      const date = new Date(this.focusDay)
      date.setDate(1)
      date.setFullYear(this.focusDay.getFullYear() + 1)
      const difference =
        (date.getTime() - this.focusDay.getTime()) / (1000 * 60 * 60 * 24)
      this.moveFocusDay(difference)
    },
    setFocusDay(date: Date): Date {
      const newDate = new Date(date)
      this.focusDay = newDate
      return this.focusDay
    },
    handleDateClick(event: MouseEvent, day: Date): void {
      const date = new Date(day.getFullYear(), day.getMonth(), day.getDate())
      this.setFocusDay(date)
      this.selectDate()
      event.preventDefault()
    },
    handleSelectButton(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof MouseEvent) {
        this.selectDate()
        event.preventDefault()
      } else if (event instanceof KeyboardEvent) {
        switch (event.key) {
          case 'Tab':
            if (!event.shiftKey) {
              ;(this.$refs.previousYearBtn as HTMLButtonElement)?.focus()
              event.preventDefault()
            }
            break
        }
      }
    },
    handleCancelButton(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof MouseEvent) {
        this.$emit('hide')
        event.preventDefault()
      } else if (event instanceof KeyboardEvent) {
        switch (event.key) {
          case 'Enter':
          case 'Space':
            this.$emit('hide')
            event.preventDefault()
            break
        }
      }
    },
    handleYearButton(event: MouseEvent | KeyboardEvent, direction = 1): void {
      if (event instanceof KeyboardEvent) {
        switch (event.key) {
          case 'Enter':
          case 'Space':
            if (direction > 0) {
              this.moveFocusToNextYear()
            } else {
              this.moveFocusToPreviousYear()
            }
            event.preventDefault()
            break
          case 'Tab':
            if (event.shiftKey && direction === -1) {
              ;(this.$refs.selectBtn as HTMLButtonElement)?.focus()
              event.preventDefault()
            }
            break
        }
      } else if (event instanceof MouseEvent) {
        if (direction > 0) {
          this.moveFocusToNextYear()
        } else {
          this.moveFocusToPreviousYear()
        }
        event.preventDefault()
      }
    },
    handleMonthButton(event: MouseEvent | KeyboardEvent, direction = 1): void {
      if (event instanceof KeyboardEvent) {
        switch (event.key) {
          case 'Enter':
          case 'Space':
            if (direction > 0) {
              this.moveFocusToNextMonth()
            } else {
              this.moveFocusToPreviousMonth()
            }
            event.preventDefault()
            break
        }
      } else if (event instanceof MouseEvent) {
        if (direction > 0) {
          this.moveFocusToNextMonth()
        } else {
          this.moveFocusToPreviousMonth()
        }
        event.preventDefault()
      }
    },
    handleGlobalKeypress(event: KeyboardEvent): void {
      switch (event.key) {
        case 'Enter':
        case 'Space':
          this.selectDate()
          event.preventDefault()
          break
        case 'Esc':
        case 'Escape':
          this.$emit('hide')
          break
        case 'Home':
          this.moveFocusToFirstDayOfWeek()
          event.preventDefault()
          break
        case 'End':
          this.moveFocusToLastDayOfWeek()
          event.preventDefault()
          break
        case 'PageUp':
          if (event.shiftKey) {
            this.moveFocusToPreviousYear()
          } else {
            this.moveFocusToPreviousMonth()
          }
          event.preventDefault()
          break
        case 'PageDown':
          if (event.shiftKey) {
            this.moveFocusToNextYear()
          } else {
            this.moveFocusToNextMonth()
          }
          event.preventDefault()
          break
      }
    },
    handleDateNavigation(event: KeyboardEvent): void {
      switch (event.key) {
        case 'Up':
        case 'ArrowUp':
          this.moveFocusDay(-7)
          event.preventDefault()
          break
        case 'Down':
        case 'ArrowDown':
          this.moveFocusDay(7)
          event.preventDefault()
          break
        case 'Right':
        case 'ArrowRight':
          this.moveFocusDay(1)
          event.preventDefault()
          break
        case 'Left':
        case 'ArrowLeft':
          this.moveFocusDay(-1)
          event.preventDefault()
          break
      }
    }
  },
  watch: {
    date(next: Date) {
      if (!next) {
        return
      }

      const date = new Date(next.getFullYear(), next.getMonth(), next.getDate())

      this.setFocusDay(date)
    }
  }
})
</script>

<template>
  <div
    class="nr-datepicker-calendar"
    @keydown="handleGlobalKeypress"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`${id}-header__label`"
  >
    <div class="nr-datepicker-calendar-header">
      <NrIconButton
        aria-label="Previous year"
        variant="minimal-neutral"
        :icon="{ name: 'chevron-double-left', type: 'regular' }"
        @click="handleYearButton($event, -1)"
        @keydown="handleYearButton($event, -1)"
        ref="previousYearBtn"
      />
      <NrIconButton
        variant="minimal-neutral"
        :icon="{ name: 'chevron-left', type: 'regular' }"
        aria-label="Previous month"
        @click="handleMonthButton($event, -1)"
        @keydown="handleMonthButton($event, -1)"
      />
      <h2
        :id="`${id}-header__label`"
        class="nr-datepicker-calendar-header__label"
        aria-live="polite"
      >
        {{ month }} {{ year }}
      </h2>
      <NrIconButton
        variant="minimal-neutral"
        :icon="{ name: 'chevron-right', type: 'regular' }"
        aria-label="Next month"
        @click="handleMonthButton($event, 1)"
        @keydown="handleMonthButton($event, 1)"
      />
      <NrIconButton
        variant="minimal-neutral"
        :icon="{ name: 'chevron-double-right', type: 'regular' }"
        aria-label="Next year"
        @click="handleYearButton($event, 1)"
        @keydown="handleYearButton($event, 1)"
        ref="nextYearBtn"
      />
    </div>

    <table
      class="nr-datepicker-calendar-dates table is-narrow"
      role="grid"
      :aria-labelledby="`${id}-header__label`"
    >
      <thead>
        <tr>
          <th class="has-text-centered" scope="col" abbr="Sunday">Su</th>
          <th class="has-text-centered" scope="col" abbr="Monday">Mo</th>
          <th class="has-text-centered" scope="col" abbr="Tuesday">Tu</th>
          <th class="has-text-centered" scope="col" abbr="Wednesday">We</th>
          <th class="has-text-centered" scope="col" abbr="Thursday">Th</th>
          <th class="has-text-centered" scope="col" abbr="Friday">Fr</th>
          <th class="has-text-centered" scope="col" abbr="Saturday">Sa</th>
        </tr>
      </thead>
      <tbody @keydown="handleDateNavigation">
        <tr
          v-for="(week, weekIndex) in getDates"
          :key="`week-${weekIndex + 1}`"
        >
          <td
            v-for="(day, dayIndex) in week"
            :key="`day ${dayIndex + 1} of week ${weekIndex}`"
            class="has-text-centered"
          >
            <NrDatepickerDay
              :day="day"
              :disabled="isDisabled(day)"
              :selected="isSelectedDate(day)"
              :focus="isFocusDate(day)"
              @click="handleDateClick($event, day)"
              ref="datesEl"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div class="nr-datepicker-calendar-actions">
      <NrButton
        variant="bordered-primary"
        ref="cancelBtn"
        @click="handleCancelButton"
        @keydown="handleCancelButton"
      >
        Cancel
      </NrButton>
      <NrButton
        ref="selectBtn"
        @click="handleSelectButton"
        @keydown="handleSelectButton"
      >
        Select date
      </NrButton>
    </div>
  </div>
</template>

<style lang="scss">
.nr-datepicker-calendar {
  max-width: 20rem;
  width: 100%;
  background-color: white;
  border: 1px solid rgba(black, 0.25);
  border-radius: 0.25rem;
  padding: 0.5em;
  position: absolute;
}

.nr-datepicker-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  padding-bottom: 1rem;
}

.nr-datepicker-calendar-header__label {
  font-size: 1.15rem;
  margin-left: auto;
  margin-right: auto;
}

.nr-datepicker-calendar-dates {
  width: 100%;

  th,
  td {
    border: none;
  }

  &.is-narrow {
    th,
    td {
      padding: 0;
    }
  }
}

.nr-datepicker-calendar-actions {
  display: flex;
  justify-content: flex-end;

  .nr-button {
    margin-left: 0.5rem;

    &:first-child {
      margin-left: unset;
    }
  }
}
</style>
